import { values } from 'lodash';
import React, { useState } from 'react';
import { ItemType } from '../../../Enums/ItemType.enum';
import { useEffectAsync } from '../../../helpers';
import HealthPlanItem from '../../../Modules/HealthPlan/Components/HealthPlanItem/HealthPlanItem';
import { useAppContext } from '../../../Services/AppContext';
import { useHealthPlanContext } from '../../../Services/HealthPlanContext';
import { useTreatmentContext } from '../../../Services/TreatmentContext';
import PlanPerMonth from '../PlanPerMonth/PlanPerMonth';
// @ts-ignore
import styles from './HealthPlanWrapper.module.scss';
import Text from '../../Base/Text/Text';
import Modal from '../../Modal/Modal';
import { HEALTH_PLAN_USER } from '../../../Modules/HealthPlan/healthPlanUser.graphlql';
import apiClient from '../../../Services/ApiClient';
import FullPageLoading from '../../Utils/FullPageLoading/FullPageLoading';
import ScrollContainer from '../../../Modules/Base/Components/ScrollContainer';
import useTranslation from '../../../Hooks/useTranslation';
import useWorkplaceRole from '../../../Modules/Workplace/Hooks/useWorkplaceRole';
import { HealthPlanUser } from '../../../graphql/graphql';
import { getHealthPlanTimeline } from '../../../Modules/HealthPlan/Services/healthPlan.service';

interface HealthPlanWrapperProps {
	data: any;
	showDetails: boolean;
	showContinue?: boolean;
	patientId?: string;
	healthPlanUser?: any;
	allowEdit?: boolean;
	workplaceId?: string;
	onSuccess?: () => void;
	wrapperCollapseTitle?: string;
	wrapperCollapse?: React.ElementType;
	setIsLoading?: (isLoading: boolean) => void;
}

const HealthPlanWrapper: React.FC<HealthPlanWrapperProps> = ({
	data: propData,
	showDetails,
	showContinue = false,
	patientId,
	healthPlanUser: hpUser,
	allowEdit = true,
	workplaceId,
	onSuccess,
	wrapperCollapse: WrapperCollapse,
	wrapperCollapseTitle,
	setIsLoading,
}) => {
	const { translation, userData } = useAppContext();
	const { isCase } = useTreatmentContext();
	const {
		healthPlan,
		healthPlanUser: healthPlanUserFromContext,
		setHealthPlanData,
		setActiveHealthPlanUserItem,
	} = useHealthPlanContext();
	const { isDoctor } = useWorkplaceRole({ userId: userData?.id, workplaceId });
	const [data, setData] = useState(propData);
	const [missing, setMissing] = useState<any[]>([]);
	const [missingItem, setMissingItem] = useState(null);
	const [fill, setFill] = useState(null);
	const [healthPlanUser, setHealthPlanUser] = useState(
		hpUser || healthPlanUserFromContext,
	);
	const [isInnerLoading, setIsInnerLoading] = useState(false);
	const { t } = useTranslation();

	useEffectAsync(async () => {
		if (typeof setIsLoading === 'function') {
			setIsLoading(false);
		} else {
			setIsInnerLoading(false);
		}
	}, [data]);

	useEffectAsync(async () => {
		if (!healthPlanUser) {
			await loadHealthPlanUser();
		}
		await loadData();
	}, []);

	const loadHealthPlanUser = async () => {
		const variables = {
			userId: patientId,
			healthPlanCode: healthPlan?.code,
			workplaceId: workplaceId,
		};

		const {
			data: { healthPlanUser },
		} = await apiClient.graphql({
			query: HEALTH_PLAN_USER,
			variables,
			fetchPolicy: 'network-only',
		});

		setHealthPlanUser(healthPlanUser);
		return healthPlanUser as HealthPlanUser;
	};

	const loadData = async (): Promise<void> => {
		if (typeof setIsLoading === 'function') {
			setIsLoading(true);
		} else {
			setIsInnerLoading(true);
		}
		if (healthPlanUser) {
			const { data: healthPlan } = await getHealthPlanTimeline({
				healthPlanId: healthPlanUser.healthPlanId,
				healthPlanUserId: healthPlanUser.id,
			});
			setData(
				Object.keys(healthPlan)
					.map(h => healthPlan[h])
					.map(item => ({
						...item,
						subTitle: t(`component.baby.timeline.${item.month}`),
					})),
			);
			setHealthPlanData(Object.keys(healthPlan).map(h => healthPlan[h]));
		}
	};

	const handleOnFill = (): void => {
		setMissing([]);
		if (fill) {
			setActiveHealthPlanUserItem(fill);
			setMissingItem(fill);
		}
	};

	const handleResetMissing = (): void => {
		setMissing([]);
	};

	const handleOnContinue = (): void => {
		const newMissing: React.SetStateAction<any[]> = [];

		data.forEach((month: { children?: any[] }, index: any) => {
			if (month.children !== undefined) {
				// TODO: add actual types for children
				month.children.forEach(
					(
						child: { extra: { status: string; doctorStatus?: string } },
						index2: any,
					) => {
						const status = child.extra?.doctorStatus ?? child.extra?.status;
						if (status === 'haveToFillNow') {
							newMissing.push(child);
						}
					},
				);
			}
		});
		if (newMissing.length >= 1) {
			setFill(newMissing[0]);
		} else {
			// NetteApiClient.get(nextStep);
			onSuccess?.();
		}

		setMissing(newMissing);
	};
	const handleMonthClick = (item: { shortTitle: any }): void => {
		setData((prevData: any[]) => {
			return prevData.map(
				(row: { shortTitle: any; collapseOpened: boolean }) => {
					if (row.shortTitle === item.shortTitle) {
						row.collapseOpened = true;
					}

					return row;
				},
			);
		});
	};

	const handleRefreshData = async (): Promise<void> => {
		await loadData();
	};

	const handleRefreshDataAfterMissing = async (): Promise<void> => {
		await loadData();
		setMissingItem(null);
	};

	return (
		<ScrollContainer>
			{isInnerLoading && <FullPageLoading isAbsolute />}
			<div className={styles.wrapper}>
				{WrapperCollapse !== undefined && (
					<WrapperCollapse
						title={wrapperCollapseTitle}
						data={values(data)}
						patientId={patientId}
						workplaceId={workplaceId}
						allowEdit={allowEdit}
						showDetails={showDetails}
						healthPlanUser={healthPlanUser}
						handleRefreshData={handleRefreshData}
						handleMonthClick={handleMonthClick}
					/>
				)}
				{showDetails && (
					<PlanPerMonth
						data={values(data)}
						patientId={patientId}
						workplaceId={workplaceId}
						reloadData={handleRefreshData}
					/>
				)}
				{showContinue && (
					<div
						className='float-panel'
						style={{ right: isCase ? '425px' : '265px' }}>
						<button
							className='btn btn-green-global remastered'
							onClick={handleOnContinue}>
							<Text tx={'base.shared.continue'} inline firstUpper />
						</button>
					</div>
				)}
				{missing.length >= 1 && (
					<Modal opened={true} onClose={handleResetMissing}>
						<Modal.Header title={translation['base.shared.notice']} />
						<Modal.Content>
							<Text
								className={'f-rubik'}
								tx={'component.healthPlan.continueCheck.title'}
							/>
							<ul className={'f-rubik mt-2'}>
								{missing.map((item, index) => (
									<li key={index}>
										<Text tx={item.title} />{' '}
										{item.type === ItemType.APPOINTMENT && (
											<>
												<Text
													tx={
														'component.healthPlanWrapper.missingMessage.part1'
													}
													inline
												/>{' '}
												{item?.extra?.month}{' '}
												<Text
													tx={
														'component.healthPlanWrapper.missingMessage.part2'
													}
													inline
												/>
											</>
										)}
									</li>
								))}
							</ul>
							<div className={'d-flex flex-row align-items-center mt-4'}>
								<button
									className={'btn btn-green-global remastered'}
									onClick={handleOnFill}>
									{translation['base.shared.continue']}
								</button>
								<div
									className={'btn-link red ml-3'}
									onClick={handleResetMissing}>
									{translation['base.shared.cancel']}
								</div>
							</div>
						</Modal.Content>
					</Modal>
				)}
				{missingItem && (
					<HealthPlanItem
						item={missingItem}
						patientId={patientId}
						workplaceId={workplaceId}
						onClose={handleRefreshDataAfterMissing}
					/>
				)}
			</div>
		</ScrollContainer>
	);
};

export default HealthPlanWrapper;
